import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import './CalendarPicker.css';

// Calendario propio para sustituir a <input type="date">. El picker nativo pinta
// los días de la semana, el mes y el botón "Limpiar" en el idioma del NAVEGADOR
// (no del i18n de la app, ni respeta el atributo lang), así que no se puede
// localizar. Este componente genera esos textos con Intl usando el locale de la
// app, de modo que siguen el idioma elegido en Ajustes.
//
// Navegación en 3 niveles (como Material): días → (pulsar título) meses →
// (pulsar año) años. Así se salta a cualquier mes/año en un par de toques.

function localeTag(lang: string | undefined) {
  return getDateLocale(lang);
}

function parseISO(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

function toISO(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

type Mode = 'days' | 'months' | 'years';

interface Props {
  /** Fecha seleccionada en formato 'YYYY-MM-DD'. */
  value: string;
  /** Se llama con la nueva fecha en formato 'YYYY-MM-DD' al pulsar un día. */
  onChange: (value: string) => void;
}

export default function CalendarPicker({ value, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const locale = localeTag(i18n.language);
  const selected = parseISO(value);
  const today = new Date();

  // Selecciona hoy y lleva la vista al mes actual en modo días (para verlo marcado).
  const goToday = () => {
    setView({ year: today.getFullYear(), month: today.getMonth() });
    setMode('days');
    onChange(toISO(today));
  };

  // Mes visible; arranca en el mes de la fecha seleccionada.
  const [view, setView] = useState<{ year: number; month: number }>({
    year: selected.getFullYear(),
    month: selected.getMonth(),
  });
  const [mode, setMode] = useState<Mode>('days');

  // --- Nombres localizados (Intl) ---
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    // 2024-01-01 fue lunes: recorremos la semana desde ahí.
    weekdayFmt.format(new Date(2024, 0, 1 + i))
  );
  const monthShortFmt = new Intl.DateTimeFormat(locale, { month: 'short' });
  const monthNames = Array.from({ length: 12 }, (_, m) => monthShortFmt.format(new Date(2024, m, 1)));

  const monthTitle = new Date(view.year, view.month, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });

  // Ventana de 12 años para la vista de años.
  const yearBase = Math.floor(view.year / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => yearBase + i);

  // --- Navegación ---
  const goMonth = (delta: number) => {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };
  const goYear = (delta: number) => setView((v) => ({ ...v, year: v.year + delta }));
  const goYearWindow = (delta: number) => setView((v) => ({ ...v, year: v.year + delta * 12 }));

  // Cabecera: flechas, título y acción del título cambian según el modo.
  const header = {
    days: { label: monthTitle, prev: () => goMonth(-1), next: () => goMonth(1), title: () => setMode('months') },
    months: { label: String(view.year), prev: () => goYear(-1), next: () => goYear(1), title: () => setMode('years') },
    years: { label: `${years[0]} – ${years[11]}`, prev: () => goYearWindow(-1), next: () => goYearWindow(1), title: () => setMode('days') },
  }[mode];

  // --- Rejilla de días ---
  const firstOfMonth = new Date(view.year, view.month, 1);
  const leadBlanks = (firstOfMonth.getDay() + 6) % 7; // lunes = 0
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: leadBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="calpick">
      <div className="calpick__header">
        <button type="button" className="calpick__nav" onClick={header.prev} aria-label="Anterior">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
        </button>
        <button type="button" className="calpick__title" onClick={header.title}>{header.label}</button>
        <button type="button" className="calpick__nav" onClick={header.next} aria-label="Siguiente">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" /></svg>
        </button>
      </div>

      {mode === 'days' && (
        <>
          <div className="calpick__grid calpick__grid--head">
            {weekdays.map((w, i) => (
              <span key={i} className="calpick__weekday">{w}</span>
            ))}
          </div>
          <div className="calpick__grid">
            {cells.map((day, i) => {
              if (day === null) return <span key={i} className="calpick__cell calpick__cell--empty" />;
              const cellDate = new Date(view.year, view.month, day);
              const isSel = sameDay(cellDate, selected);
              const isToday = sameDay(cellDate, today);
              return (
                <button
                  key={i}
                  type="button"
                  className={`calpick__cell${isSel ? ' calpick__cell--selected' : ''}${isToday && !isSel ? ' calpick__cell--today' : ''}`}
                  onClick={() => onChange(toISO(cellDate))}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </>
      )}

      {mode === 'months' && (
        <div className="calpick__grid calpick__grid--my">
          {monthNames.map((name, m) => {
            const isSel = selected.getFullYear() === view.year && selected.getMonth() === m;
            const isCurrent = today.getFullYear() === view.year && today.getMonth() === m;
            return (
              <button
                key={m}
                type="button"
                className={`calpick__mcell${isSel ? ' calpick__mcell--selected' : ''}${isCurrent && !isSel ? ' calpick__mcell--today' : ''}`}
                onClick={() => { setView((v) => ({ ...v, month: m })); setMode('days'); }}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      {mode === 'years' && (
        <div className="calpick__grid calpick__grid--my">
          {years.map((y) => {
            const isSel = selected.getFullYear() === y;
            const isCurrent = today.getFullYear() === y;
            return (
              <button
                key={y}
                type="button"
                className={`calpick__mcell${isSel ? ' calpick__mcell--selected' : ''}${isCurrent && !isSel ? ' calpick__mcell--today' : ''}`}
                onClick={() => { setView((v) => ({ ...v, year: y })); setMode('months'); }}
              >
                {y}
              </button>
            );
          })}
        </div>
      )}

      <div className="calpick__footer">
        <button type="button" className="calpick__today" onClick={goToday}>
          {t('common.today')}
        </button>
      </div>
    </div>
  );
}
